import React, { useState, useEffect } from 'react';
import { FiActivity, FiFileText, FiDollarSign, FiArrowLeft, FiUser, FiPhone, FiMapPin, FiCalendar, FiExternalLink } from 'react-icons/fi';
import { api } from '@crm/shared';
import { C, fmtAmt, STAGE_CFG } from '../shared/tokens';
import { Sh, Kpi, Err, Empty } from '../shared/ui';
import { exportCsv, exportExcel, ExportButton } from '../shared/ExportButton';
import { monthLabel } from '../shared/MisPeriodBar';
import { useNavigate } from 'react-router-dom';

const fmtDate = s => {
  if (!s) return '—';
  try { const d = new Date(s); return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`; }
  catch { return s; }
};

const StagePill = ({ stage }) => {
  const cfg = STAGE_CFG[stage] || { bg:'#F1F5F9', color:'#6B7280' };
  const labels = { FOLLOW_UP:'Follow Up', LOGIN:'Login', SANCTION:'Sanction', DISBURSEMENT:'Disbursement', PDD:'PDD', PDD_PENDING:'PDD Pending', REVENUE:'Revenue', COMPLETE:'Completed', CLOSED:'Closed' };
  return <span style={{ display:'inline-flex', padding:'2px 9px', borderRadius:999, fontSize:11, fontWeight:700, background:cfg.bg, color:cfg.color, whiteSpace:'nowrap' }}>{labels[stage] || stage}</span>;
};

const Val = ({ v }) => v ? <span>{v}</span> : <span style={{ color:C.t4 }}>—</span>;

// ── DRILL-DOWN TABLE ──────────────────────────────────────────────────────────
function DrillDownTable({ rows, title, onBack, year, month, loading, error, onRetry }) {
  const navigate = useNavigate();
  const [exporting, setExporting] = useState(false);

  const COLS = ['Lead No','Customer','Mobile','Email','Location','Product','Lead Source',
    'Loan Amt','Lead Date','Login Date','App/LOS No','Institution','Channel','Banker',
    'Sanctioned Amt','Sanction Date','Exp Disb Date',
    'Disbursed Amt','Disb Date','Disb Type','Stage','Executive','Created By','BSA'];
  const toRow = r => [
    r.lead_no, r.customer_name, r.mobile_number, r.email, r.location, r.product, r.lead_source,
    r.loan_amount, r.lead_date, r.login_date, r.application_number || r.los_number || '', r.institution, r.channel, r.banker,
    r.sanctioned_amount, r.sanction_date, r.expected_disb_date,
    r.disbursed_amount, r.disbursement_date, r.disbursement_type, r.current_stage,
    r.executive, r.created_by_name, r.bsa_name,
  ];
  const fname = `channel_drill_${year}_${month}`;
  const handleExport = fmt => {
    setExporting(true);
    const data = rows.map(toRow);
    if (fmt === 'excel') exportExcel(COLS, data, fname, title);
    else exportCsv(COLS, data, fname);
    setExporting(false);
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
        <button onClick={onBack}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:9,
            background:C.white, border:`1.5px solid ${C.border}`, color:C.navy, fontSize:13,
            fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
          <FiArrowLeft size={13}/> Back to Summary
        </button>
        <div style={{ flex:1 }}>
          <div style={{ fontSize:15, fontWeight:800, color:C.t1 }}>{title}</div>
          <div style={{ fontSize:12, color:C.t3, marginTop:2 }}>{rows.length} leads found</div>
        </div>
        <ExportButton onExport={handleExport} loading={exporting} size="sm"/>
      </div>

      {loading && <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:18 }}>
        {[1,2,3,4,5].map(i => <Sh key={i} h={48} mb={8}/>)}
      </div>}

      {error && <Err msg={error} onRetry={onRetry}/>}

      {!loading && !error && rows.length === 0 && <Empty label="No leads found for this selection"/>}

      {!loading && !error && rows.length > 0 && (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:1600 }}>
              <thead>
                <tr style={{ background:'#F7F9FB' }}>
                  {['#','Lead No','Customer Name','Mobile','Email','Location','Product','Src',
                    'Loan Amt','Lead Date','Login Date','App/LOS No','Institution','Channel','Banker',
                    'Sanctioned','Sanction Dt','Exp Disb Dt',
                    'Disbursed','Disb Date','Disb Type','Stage','Executive','Created By','BSA'
                  ].map((h, i) => (
                    <th key={h} style={{ padding:'9px 12px', fontSize:10, fontWeight:800, color:C.t2,
                      textTransform:'uppercase', letterSpacing:'0.06em', textAlign:'left',
                      borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap',
                      background: i >= 14 && i <= 18 ? '#F0FFF4' : i >= 19 && i <= 21 ? '#EFF6FF' : '#F7F9FB',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const isLast = idx === rows.length - 1;
                  const bg = idx % 2 === 0 ? C.white : '#FAFBFC';
                  return (
                    <tr key={r.lead_id || idx}
                      style={{ background:bg }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F0FFFE'}
                      onMouseLeave={e => e.currentTarget.style.background = bg}>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:11, color:C.t4 }}>{idx+1}</td>
                      {/* Lead No — clickable */}
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}>
                        <button onClick={() => navigate(`/leads/${r.lead_id}`)}
                          style={{ background:'none', border:'none', cursor:'pointer', padding:0, display:'flex', alignItems:'center', gap:4, color:C.navy, fontFamily:'monospace', fontSize:12, fontWeight:700, textDecoration:'underline' }}>
                          {r.lead_no}<FiExternalLink size={10}/>
                        </button>
                      </td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:13, fontWeight:700, color:C.t1, whiteSpace:'nowrap' }}>{r.customer_name}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2, fontFamily:'monospace' }}>{r.mobile_number}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3, maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}><Val v={r.email}/></td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3 }}><Val v={r.location}/></td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2 }}><Val v={r.product}/></td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:11 }}>
                        <span style={{ background:r.lead_source==='DIRECT'?C.skyL:C.purpleL, color:r.lead_source==='DIRECT'?C.sky:C.purple, padding:'2px 7px', borderRadius:999, fontSize:10, fontWeight:700 }}>{r.lead_source||'—'}</span>
                      </td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, fontWeight:700, color:C.navy, whiteSpace:'nowrap' }}>{fmtAmt(r.loan_amount)}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3, whiteSpace:'nowrap' }}>{fmtDate(r.lead_date)}</td>
                      {/* Login section */}
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2, whiteSpace:'nowrap' }}>{fmtDate(r.login_date)}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3, fontFamily:'monospace' }}><Val v={r.application_number || r.los_number}/></td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2, whiteSpace:'nowrap' }}><Val v={r.institution}/></td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.teal, fontWeight:600 }}><Val v={r.channel}/></td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3 }}><Val v={r.banker}/></td>
                      {/* Sanction section — green tint */}
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, fontWeight:700, color:C.green, background:'#F0FFF4', whiteSpace:'nowrap' }}>{r.sanctioned_amount ? fmtAmt(r.sanctioned_amount) : '—'}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3, background:'#F0FFF4', whiteSpace:'nowrap' }}>{fmtDate(r.sanction_date)}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3, background:'#F0FFF4', whiteSpace:'nowrap' }}>{fmtDate(r.expected_disb_date)}</td>
                      {/* Disbursement section — blue tint */}
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, fontWeight:700, color:C.sky, background:'#EFF6FF', whiteSpace:'nowrap' }}>{r.disbursed_amount ? fmtAmt(r.disbursed_amount) : '—'}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3, background:'#EFF6FF', whiteSpace:'nowrap' }}>{fmtDate(r.disbursement_date)}</td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, background:'#EFF6FF' }}>
                        {r.disbursement_type ? <span style={{ background:C.skyL, color:C.sky, padding:'2px 7px', borderRadius:999, fontSize:10, fontWeight:700 }}>{r.disbursement_type}</span> : <span style={{ color:C.t4 }}>—</span>}
                      </td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}` }}><StagePill stage={r.current_stage}/></td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t2, whiteSpace:'nowrap' }}><Val v={r.executive}/></td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3, whiteSpace:'nowrap' }}><Val v={r.created_by_name}/></td>
                      <td style={{ padding:'9px 12px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3 }}><Val v={r.bsa_name}/></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function ChannelWiseMIS({ filters = {}, loadTrigger = 0, setLoading, searched }) {
  const { year, month, dateFrom, dateTo, channelFilter, channels = [] } = filters;

  const [allRows,    setAllRows]    = useState([]);
  const [localLoad,  setLocalLoad]  = useState(false);
  const [error,      setError]      = useState(null);
  const [exporting,  setExporting]  = useState(false);

  // Drill-down state
  const [drillDown,   setDrillDown]   = useState(null); // { channel_id, channel_name }
  const [drillRows,   setDrillRows]   = useState([]);
  const [drillLoad,   setDrillLoad]   = useState(false);
  const [drillError,  setDrillError]  = useState(null);

  const label = monthLabel(year, month);
  const hasDateRange = Boolean(dateFrom && dateTo);
  const periodLabel = hasDateRange ? `${dateFrom} to ${dateTo}` : label;

  const load = async () => {
    if (!year || !month) return;
    setLocalLoad(true); setLoading?.(true); setError(null);
    try {
      const res = await api.getMisChannelWise({
        year,
        month,
        ...(channelFilter ? { channel: channelFilter } : {}),
        ...(hasDateRange ? { from_date: dateFrom, to_date: dateTo } : {}),
      });
      const d = res?.data;
      setAllRows(Array.isArray(d?.results) ? d.results : []);
    } catch (e) { setError(e?.response?.data?.detail || e?.message || 'Failed to load Channel Wise MIS'); }
    finally { setLocalLoad(false); setLoading?.(false); }
  };

  useEffect(() => { if (loadTrigger > 0) load(); }, [loadTrigger]); // eslint-disable-line

  // When tab changes or filter changes, reset drill-down
  useEffect(() => { setDrillDown(null); setDrillRows([]); }, [loadTrigger]);

  const loadDrillDown = async (channel_id, channel_name) => {
    setDrillDown({ channel_id, channel_name });
    setDrillLoad(true); setDrillError(null); setDrillRows([]);
    try {
      const res = await api.getMisChannelWiseLeads({
        channel_id,
        year,
        month,
        ...(hasDateRange ? { from_date: dateFrom, to_date: dateTo } : {}),
      });
      setDrillRows(Array.isArray(res?.data?.results) ? res.data.results : []);
    } catch (e) { setDrillError(e?.response?.data?.detail || e?.message || 'Failed to load leads'); }
    finally { setDrillLoad(false); }
  };

  // Client-side filtering
  const rows = allRows.filter(r => {
    if (channelFilter) {
      const ch = channels.find(c => String(c.id) === String(channelFilter));
      if (ch) {
        const nameMatch = (r.channel_name || '').toLowerCase().includes((ch.name || '').toLowerCase());
        const codeMatch = (r.channel_code || '').toLowerCase() === (ch.code || '').toLowerCase();
        if (!nameMatch && !codeMatch) return false;
      }
    }
    return true;
  });

  const totalDisb  = rows.reduce((s, r) => s + parseFloat(r.total_disb_amount || 0), 0);
  const totalLogin = rows.reduce((s, r) => s + Number(r.login_count || 0), 0);

  const COLS = ['Channel Name','Channel Code','Login Count','Total Disb Amount'];
  const toRow = r => [r.channel_name||'', r.channel_code||'', r.login_count||0, r.total_disb_amount||'0'];
  const selChannelName = channelFilter
    ? (channels.find(c => String(c.id) === String(channelFilter))?.name || '')
    : '';
  const title = selChannelName ? `Channel Wise MIS - ${selChannelName}` : `Channel Wise MIS - ${periodLabel}`;
  const fname = selChannelName ? `channel_wise_${selChannelName.replace(/\s+/g,'_')}_${year}_${month}` : `channel_wise_${year}_${month}`;

  const handleExport = fmt => {
    setExporting(true);
    const data = rows.map(toRow);
    if (fmt === 'excel') exportExcel(COLS, data, fname, title);
    else exportCsv(COLS, data, fname);
    setExporting(false);
  };

  // ── Drill-Down View ──
  if (drillDown) {
    return (
      <DrillDownTable
        rows={drillRows}
        title={`${drillDown.channel_name} — ${periodLabel}`}
        onBack={() => { setDrillDown(null); setDrillRows([]); }}
        year={year} month={month}
        loading={drillLoad}
        error={drillError}
        onRetry={() => loadDrillDown(drillDown.channel_id, drillDown.channel_name)}
      />
    );
  }

  // ── Not yet searched ──
  if (!searched) return (
    <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:'52px 24px', textAlign:'center' }}>
      <div style={{ width:54, height:54, borderRadius:'50%', background:C.tealL, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
        <FiActivity size={22} style={{ color:C.tealB, opacity:0.6 }}/>
      </div>
      <div style={{ fontSize:14, fontWeight:700, color:C.t1, marginBottom:6 }}>Select period and click "Load Report"</div>
      <div style={{ fontSize:12.5, color:C.t3 }}>Channel wise data will appear here</div>
    </div>
  );

  if (localLoad) return (
    <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, padding:18 }}>
      <div style={{ display:'flex', flexDirection:'column', gap:10 }}>{[0,1,2,3].map(i => <Sh key={i} h={48}/>)}</div>
    </div>
  );

  if (error) return <Err msg={error} onRetry={load}/>;

  return (
    <>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:14 }}>
        <Kpi label="Channels Active"     value={rows.length}       icon={<FiActivity size={15}/>}   iconBg={C.tealL}  iconColor={C.tealB}  accent={C.tealB}  loading={false} sub={periodLabel}/>
        <Kpi label="Total Logins (THRU)" value={totalLogin}        icon={<FiFileText size={15}/>}   iconBg={C.navyL}  iconColor={C.navy}   accent={C.navy}   loading={false}/>
        <Kpi label="Total Disbursed"     value={fmtAmt(totalDisb)} icon={<FiDollarSign size={15}/>} iconBg={C.greenL} iconColor={C.greenB} accent={C.greenB} loading={false}/>
      </div>

      {rows.length === 0 ? <Empty label={`No channel data found for ${periodLabel}`}/> : (
        <div style={{ background:C.white, borderRadius:12, border:`1px solid ${C.border}`, overflow:'hidden', boxShadow:'0 2px 6px rgba(13,59,94,0.06)' }}>
          <div style={{ padding:'11px 18px', borderBottom:`1px solid ${C.borderL}`, background:'#FAFBFC', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:8 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <FiActivity size={14} style={{ color:C.tealB }}/>
              <span style={{ fontSize:13.5, fontWeight:700, color:C.t1 }}>Channel Wise — {periodLabel}</span>
              <span style={{ background:C.tealL, color:C.tealB, padding:'2px 8px', borderRadius:999, fontSize:10.5, fontWeight:700 }}>{rows.length} channels</span>
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <span style={{ fontSize:11.5, color:C.t4 }}>Click count to see leads</span>
              <ExportButton onExport={handleExport} loading={exporting} size="sm"/>
            </div>
          </div>
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:600 }}>
              <thead>
                <tr style={{ background:'#F7F9FB' }}>
                  {['#','Channel Name','Channel Code','Login Count','Total Disb Amount'].map((h, i) => (
                    <th key={h} style={{ padding:'10px 14px', fontSize:10.5, fontWeight:800, color:C.t2, textTransform:'uppercase', letterSpacing:'0.07em', textAlign:i >= 3 ? 'right' : 'left', borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => {
                  const isLast = idx === rows.length - 1;
                  return (
                    <tr key={r.channel_id || idx} style={{ background:idx%2===0?C.white:'#FAFBFC' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F0FFFE'}
                      onMouseLeave={e => e.currentTarget.style.background = idx%2===0?C.white:'#FAFBFC'}>
                      <td style={{ padding:'10px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:11.5, color:C.t4 }}>{idx+1}</td>
                      <td style={{ padding:'10px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:13, fontWeight:700, color:C.t1 }}>{r.channel_name||'—'}</td>
                      <td style={{ padding:'10px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, fontSize:12, color:C.t3, fontFamily:'monospace' }}>{r.channel_code||'—'}</td>
                      {/* Clickable Login Count */}
                      <td style={{ padding:'10px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right' }}>
                        {r.channel_id ? (
                          <button
                            onClick={() => loadDrillDown(r.channel_id, r.channel_name)}
                            style={{ background:C.purpleL, color:C.purple, border:`1px solid ${C.purpleB}20`, borderRadius:8, padding:'4px 12px', fontSize:13, fontWeight:800, cursor:'pointer', fontFamily:'inherit', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:5, transition:'all 0.12s' }}
                            onMouseEnter={e => { e.currentTarget.style.background = C.purple; e.currentTarget.style.color = '#fff'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = C.purpleL; e.currentTarget.style.color = C.purple; }}>
                            {r.login_count||0}
                          </button>
                        ) : (
                          <span style={{ fontSize:13, fontWeight:700, color:C.purple }}>{r.login_count||0}</span>
                        )}
                      </td>
                      <td style={{ padding:'10px 14px', borderBottom:isLast?'none':`1px solid ${C.borderL}`, textAlign:'right', fontSize:13, fontWeight:800, color:C.greenB }}>{fmtAmt(r.total_disb_amount)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background:'#F7F9FB', borderTop:`2px solid ${C.border}` }}>
                  <td colSpan={3} style={{ padding:'10px 14px', fontSize:12, fontWeight:800, color:C.t2 }}>TOTAL ({rows.length} channels)</td>
                  <td style={{ padding:'10px 14px', textAlign:'right', fontSize:13, fontWeight:800, color:C.purple }}>{totalLogin}</td>
                  <td style={{ padding:'10px 14px', textAlign:'right', fontSize:13, fontWeight:800, color:C.greenB }}>{fmtAmt(totalDisb)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
